import { useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { Textarea } from '@/components/form/Textarea';
import { SearchableSelect } from '@/components/form/SearchableSelect';
import { ymd } from '@/components/ui/PeriodPicker';
import { BANK_MODES, getPending, type LedgerOption, type PendingBill, type VoucherInput } from '@/api/accountsBooks';
import { ChipTag, DateField, F, FormFooter, FormTitle, Msgs, Posting, ReadBox, Sec, SmBtn, XBtn, type PostLine } from './voucherFormParts';
import { cashBank, curBal, daysBetween, dd, inr, r2, type Ref } from './voucherFormat';
import { useSaveVoucher, type FormProps } from './useVoucherForm';

/* Receipt & Payment — laid out like Tally's vouchers (F6 / F5), as the prototype:
   Account → Particulars (one or more parties, each with an amount) → Bill-wise
   Details per party (Agst Ref / New Ref / Advance / On Account) → Bank Allocation
   → Narration → Posting. */

const KIND = {
  Receipt: { chip: <ChipTag tone="g">Money in</ChipTag>, role: 'D', refSide: 'Cr', billSide: 'Dr', verb: 'received', narr: 'Received from', partyDr: false, nowCol: 'Receive now' },
  Payment: { chip: <ChipTag tone="r">Money out</ChipTag>, role: 'C', refSide: 'Dr', billSide: 'Cr', verb: 'paid', narr: 'Paid to', partyDr: true, nowCol: 'Pay now' },
} as const;

interface Row { key: number; party: number | ''; amount: number; refs: Ref[] }
interface State {
  date: string; via: number | ''; mode: string; ref: string; idate: string; charges: number;
  bankName: string; favouring: string; narr: string; rows: Row[];
}
let seq = 1;
const modeOpts = (acc?: LedgerOption) => (acc?.kind === 'cash' ? ['Cash'] : [...BANK_MODES]);
const sum = (refs: Ref[]) => r2(refs.reduce((s, x) => s + x.amt, 0));

export function ReceiptPaymentForm({ type, voucher, ledgers, nextNo, onClose, onChallan }: FormProps & { type: 'Receipt' | 'Payment'; onChallan?: () => void }) {
  const k = KIND[type];
  const byId = useMemo(() => new Map(ledgers.map((l) => [l.id, l])), [ledgers]);
  const accounts = ledgers.filter(cashBank);
  const parties = ledgers.filter((l) => l.role === k.role);
  const bankCharges = ledgers.find((l) => l.name === 'Bank Charges');

  // Starting state: a new voucher, or the one being altered taken apart into the form's fields.
  const [s, setS] = useState<State>(() => {
    const today = ymd(new Date());
    if (!voucher) {
      const acc = accounts.find((a) => a.kind === 'bank') ?? accounts[0];
      return { date: today, via: acc?.id ?? ('' as number | ''), mode: modeOpts(acc)[0], ref: '', idate: today, charges: 0, bankName: '', favouring: '', narr: '', rows: [{ key: seq++, party: '' as number | '', amount: 0, refs: [] as Ref[] }] };
    }
    const accLine = voucher.lines.find((l) => l.side === (k.partyDr ? 'Cr' : 'Dr') && cashBank(byId.get(l.ledgerId)));
    const chargeLine = voucher.lines.find((l) => l.ledgerId === bankCharges?.id && l.side === 'Dr');
    return {
      date: voucher.date, via: accLine?.ledgerId ?? '', mode: voucher.mode ?? 'NEFT', ref: voucher.refNo ?? '', idate: voucher.instrDate ?? voucher.date,
      charges: type === 'Receipt' ? chargeLine?.amount ?? 0 : 0, bankName: voucher.bankName ?? '', favouring: voucher.favouring ?? '', narr: voucher.narration ?? '',
      rows: voucher.lines.filter((l) => l !== accLine && l !== chargeLine).map((l) => ({
        key: seq++, party: l.ledgerId, amount: l.amount,
        refs: l.bills.map((b) => ({ t: b.refType, name: b.billNo ?? '', amt: b.amount })),
      })),
    };
  });
  const set = (p: Partial<State>) => setS((x) => ({ ...x, ...p }));
  const setRow = (i: number, p: Partial<Row>) => setS((x) => ({ ...x, rows: x.rows.map((r, j) => (j === i ? { ...r, ...p } : r)) }));

  // Each party's open bills (as they stood without this voucher, when altering).
  const pend = useQueries({
    queries: s.rows.map((r) => ({
      queryKey: ['acc-books', 'pending', r.party, voucher?.id ?? 0],
      queryFn: () => getPending(r.party as number, voucher?.id),
      enabled: r.party !== '',
    })),
  });
  // Bills this voucher settles: a Receipt (Cr) settles what customers owe (+), a Payment what we owe suppliers (−).
  const openOf = (i: number): PendingBill[] => (pend[i]?.data?.bills ?? []).filter((b) => (k.partyDr ? b.pending < 0 : b.pending > 0));
  const pendingOf = (b: PendingBill) => Math.abs(b.pending);

  const acc = s.via === '' ? undefined : byId.get(s.via);
  const isCash = acc?.kind === 'cash';
  const total = r2(s.rows.reduce((t, r) => t + r.amount, 0));
  const charges = type === 'Receipt' && !isCash ? r2(s.charges || 0) : 0;
  const net = r2(total - charges);

  /** Oldest pending bills first as Agst Ref lines; whatever is left goes On Account. */
  const fifo = (i: number, amount: number): Ref[] => {
    const refs: Ref[] = [];
    let left = amount;
    for (const b of openOf(i)) {
      if (left <= 0.004) break;
      const a = r2(Math.min(left, pendingOf(b)));
      refs.push({ t: 'Agst Ref', name: b.billNo, amt: a });
      left = r2(left - a);
    }
    if (left > 0.004) refs.push({ t: 'On Account', name: '', amt: left });
    return refs;
  };
  // The party amount is always what its bill-wise lines add up to.
  const setRefs = (i: number, refs: Ref[]) => setRow(i, { refs, amount: sum(refs) });
  const tick = (i: number, b: PendingBill, on: boolean) => {
    const refs = s.rows[i].refs.filter((y) => !(y.t === 'Agst Ref' && y.name === b.billNo));
    setRefs(i, on ? [...refs, { t: 'Agst Ref', name: b.billNo, amt: pendingOf(b) }] : refs);
  };
  const tickAll = (i: number, on: boolean) => {
    const refs = s.rows[i].refs.filter((y) => y.t !== 'Agst Ref');
    setRefs(i, on ? [...refs, ...openOf(i).map((b) => ({ t: 'Agst Ref' as const, name: b.billNo, amt: pendingOf(b) }))] : refs);
  };
  const setRef = (i: number, j: number, p: Partial<Ref>) => setRefs(i, s.rows[i].refs.map((y, m) => (m === j ? { ...y, ...p } : y)));

  // What stops the save, and what only warns — as the prototype checks.
  const block: string[] = [];
  const warn: string[] = [];
  const today = ymd(new Date());
  if (!s.date) block.push('Enter the date.');
  else if (s.date > today) block.push(`The date can’t be after today (${dd(today)}).`);
  if (!acc) block.push('Choose the Account (cash or bank).');
  if (!(total > 0)) block.push(`Enter the amount ${k.verb}.`);
  const chosen = s.rows.filter((r) => r.party !== '').map((r) => r.party);
  if (new Set(chosen).size < chosen.length) block.push('The same party is entered twice — put it on one line.');
  if (s.rows.some((r) => r.party === '' && r.amount > 0)) block.push('Choose the party on every line.');
  if (!isCash && acc) {
    if (!s.ref.trim()) block.push(`Bank Allocation: enter the ${s.mode === 'Cheque' ? 'cheque' : 'UTR / reference'} number.`);
    if (!s.idate) block.push('Bank Allocation: enter the instrument date.');
    else if (s.mode !== 'Cheque' && s.idate > s.date) block.push(`Bank Allocation: the ${s.mode} date can't be after the voucher date (${dd(s.date)}).`);
  }
  if (charges && !(net > 0)) block.push('Bank charges are more than the amount received — check them.');
  if (charges && !bankCharges) block.push('The Bank Charges ledger is missing — create it under Indirect Expenses.');
  s.rows.forEach((r, i) => {
    const name = r.party === '' ? 'Party' : byId.get(r.party)?.name ?? 'Party';
    const alloc = sum(r.refs), diff = r2(r.amount - alloc);
    if (r.amount > 0 && Math.abs(diff) > 0.004) block.push(`${name}: bill-wise details add up to ${inr(alloc)}, not ${inr(r.amount)}.`);
    r.refs.forEach((x) => {
      if (x.amt > 0 && x.t === 'Agst Ref' && !x.name) block.push(`${name}: pick the bill for the Agst Ref line.`);
      if (x.amt > 0 && (x.t === 'New Ref' || x.t === 'Advance') && !x.name.trim()) block.push(`${name}: give the ${x.t} a name.`);
      const b = x.t === 'Agst Ref' && openOf(i).find((y) => y.billNo === x.name);
      if (b && x.amt > pendingOf(b) + 0.004) block.push(`${name}: ${x.name} has only ${inr(pendingOf(b))} pending.`);
    });
    if (type === 'Payment' && isCash && r.amount > 10000) warn.push(`${name}: cash payment above ₹10,000 to one person in a day is not allowed as a business expense (s.40A(3)). Pay by bank.`);
  });

  // The voucher as it will post.
  const sgn = k.partyDr ? 1 : -1;
  const partyLines: PostLine[] = s.rows.filter((r) => r.party !== '' && r.amount > 0).map((r) => ({
    l: byId.get(r.party as number)!.name, a: sgn * r.amount,
    sub: r.refs.filter((x) => x.amt > 0).map((x) => `${x.t}${x.name ? ` ${x.name}` : ''} · ${inr(x.amt)}`),
  }));
  const accLine: PostLine = { l: acc?.name ?? '', a: -sgn * net };
  const post: PostLine[] = k.partyDr
    ? [...partyLines, accLine] // Payment: party Dr first, bank Cr last
    : [accLine, ...(charges ? [{ l: 'Bank Charges', a: charges }] : []), ...partyLines];

  const build = (): VoucherInput => {
    const rows = s.rows.filter((r) => r.party !== '' && r.amount > 0);
    const lines = rows.map((r) => ({
      ledgerId: r.party as number, side: (k.partyDr ? 'Dr' : 'Cr') as 'Dr' | 'Cr', amount: r.amount,
      bills: r.refs.filter((x) => x.amt > 0).map((x) => ({ refType: x.t, billNo: x.t === 'On Account' ? null : x.name.trim(), amount: x.amt })),
    }));
    const account = { ledgerId: s.via as number, side: (k.partyDr ? 'Cr' : 'Dr') as 'Dr' | 'Cr', amount: net };
    const all = k.partyDr ? [...lines, account] : [account, ...(charges ? [{ ledgerId: bankCharges!.id, side: 'Dr' as const, amount: charges }] : []), ...lines];
    const names = rows.map((r) => byId.get(r.party as number)?.name).join(', ');
    return {
      date: s.date, narration: s.narr.trim() || `${k.narr} ${names}`, lines: all,
      ...(isCash ? {} : {
        mode: s.mode, refNo: s.ref.trim(), instrDate: s.idate,
        ...(type === 'Receipt' && s.mode === 'Cheque' ? { bankName: s.bankName.trim() } : {}),
        ...(type === 'Payment' ? { favouring: s.favouring.trim() } : {}),
      }),
    };
  };
  const save = useSaveVoucher(type, voucher, onClose);

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={
        <FormTitle title={type} no={voucher?.voucherNo ?? nextNo} chip={k.chip}
          extra={type === 'Payment' && onChallan ? (
            <span className="ml-auto inline-flex overflow-hidden rounded-md border border-[var(--border-color)] text-[11px] font-bold">
              <span className="bg-accent/15 px-2.5 py-1 text-accent">Payment</span>
              <button type="button" onClick={onChallan} className="px-2.5 py-1 text-[var(--text-muted)] hover:text-accent">TDS challan</button>
            </span>
          ) : undefined} />
      }
      footer={
        <FormFooter
          left={<>Total <b>{inr(total)}</b>{charges ? <> · in bank <b>{inr(net)}</b></> : null}</>}
          version={voucher ? voucher.version + 1 : undefined}
          saveLabel={`Save ${type}`} blocked={block.length > 0} saving={save.isPending}
          onClose={onClose} onSave={() => save.mutate(build())}
        />
      }
    >
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <DateField value={s.date} max={today} onChange={(v) => set({ date: v, ...(s.idate === s.date ? { idate: v } : {}) })} />
        <F label="Account *" className="col-span-2">
          <Select value={s.via} onChange={(e) => { const a = byId.get(Number(e.target.value)); set({ via: Number(e.target.value), mode: modeOpts(a)[0] }); }}>
            {s.via === '' && <option value="">— choose —</option>}
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </Select>
        </F>
        <F label="Cur Bal"><ReadBox value={curBal(acc)} /></F>
      </div>
      {accounts.length === 0 && <p className="mt-2 text-xs text-warning">There is no cash or bank account — create one in Ledgers (under Bank Accounts or Cash-in-Hand).</p>}
      {type === 'Payment' && (
        <div className="mt-3 rounded-md border border-info/30 bg-info/10 px-2.5 py-1.5 text-xs text-[var(--text-secondary)]">
          TDS is deducted when the bill is booked, so bills here are already net of TDS — pay the pending amount.
        </div>
      )}

      <Sec>Particulars</Sec>
      {s.rows.map((r, i) => {
        const party = r.party === '' ? undefined : byId.get(r.party);
        const ob = openOf(i);
        const alloc = sum(r.refs), diff = r2(r.amount - alloc);
        const ticked = new Map(r.refs.filter((y) => y.t === 'Agst Ref').map((y) => [y.name, y]));
        const allOn = ob.length > 0 && ob.every((b) => ticked.has(b.billNo));
        const other = r.refs.map((x, j) => [x, j] as const).filter(([x]) => x.t !== 'Agst Ref');
        return (
          <div key={r.key} className="mb-2.5 rounded-xl border border-[var(--border-color)] p-2.5 sm:p-3">
            <div className="flex flex-wrap items-end gap-2.5">
              <F label="Party" className="min-w-[220px] flex-1">
                <SearchableSelect
                  options={parties.map((p) => ({ value: p.id, label: p.name, sublabel: [p.partyCode, p.group].filter(Boolean).join(' · ') }))}
                  value={r.party} placeholder={type === 'Receipt' ? 'Customer' : 'Supplier'}
                  onChange={(v) => setRow(i, { party: v === '' ? '' : Number(v), refs: [], amount: 0 })}
                />
              </F>
              <F label="Amount" className="w-[150px]">
                <Input type="number" step="0.01" min={0} className="text-right font-mono" value={r.amount || ''} placeholder="0.00"
                  onChange={(e) => { const a = r2(Number(e.target.value) || 0); setRow(i, { amount: a, refs: fifo(i, a) }); }} />
              </F>
              {s.rows.length > 1 && <SmBtn title="Remove party" onClick={() => set({ rows: s.rows.filter((_, j) => j !== i) })}>✕</SmBtn>}
            </div>
            {party && (
              <>
                <div className="mb-2 mt-1 text-[11px] text-[var(--text-muted)]">
                  Cur Bal: <b className="text-[var(--text-primary)]">{curBal(party)}</b> · {ob.length} pending bill{ob.length === 1 ? '' : 's'}
                  {type === 'Payment' && party.bank && <> · Pay to A/c <b className="font-mono">{party.bank.account}</b>{party.bank.ifsc && <> · IFSC <b className="font-mono">{party.bank.ifsc}</b></>}{party.bank.name && <> · {party.bank.name}</>}</>}
                </div>
                <div className="mb-1 text-[11px] font-extrabold uppercase tracking-wider text-[var(--text-secondary)]">
                  Bill-wise Details for {party.name}{' '}
                  <span className="font-semibold normal-case tracking-normal">
                    — Upto {inr(r.amount)} {k.refSide}
                    {Math.abs(diff) > 0.004 && <> · <span className="text-warning">{diff > 0 ? `${inr(diff)} still to allocate` : `${inr(-diff)} over`}</span></>}
                  </span>
                </div>
                <div className="overflow-x-auto rounded-lg border border-[var(--border-color)]">
                  <table className="w-full min-w-[620px] whitespace-nowrap text-xs">
                    <thead className="bg-white/[0.03] text-left text-[var(--text-muted)]">
                      <tr>
                        <th className="w-[34px] px-2 py-1.5">{ob.length > 0 && <input type="checkbox" className="h-4 w-4 cursor-pointer" checked={allOn} title="Tick all bills" aria-label="Tick all bills" onChange={(e) => tickAll(i, e.target.checked)} />}</th>
                        <th className="w-[90px] px-2 py-1.5">Type of Ref</th><th className="px-2 py-1.5">Bill no.</th><th className="px-2 py-1.5">Bill date</th><th className="px-2 py-1.5">Due on</th>
                        <th className="px-2 py-1.5 text-right">Pending</th><th className="px-2 py-1.5 text-right">{k.nowCol}</th><th className="px-2 py-1.5" />
                      </tr>
                    </thead>
                    <tbody>
                      {ob.map((b) => {
                        const x = ticked.get(b.billNo);
                        const od = b.dueDate ? daysBetween(b.dueDate, s.date) : 0;
                        return (
                          <tr key={b.billNo} className={`border-t border-[var(--border-color)] ${x ? 'bg-accent/[0.07]' : ''}`}>
                            <td className="px-2 py-1.5"><input type="checkbox" className="h-4 w-4 cursor-pointer" checked={!!x} aria-label={`Tick ${b.billNo}`} onChange={(e) => tick(i, b, e.target.checked)} /></td>
                            <td className="px-2 py-1.5">{x ? <b>Agst Ref</b> : '—'}</td>
                            <td className="cursor-pointer px-2 py-1.5 font-mono" onClick={() => tick(i, b, !x)}>{b.billNo}</td>
                            <td className="px-2 py-1.5">{dd(b.billDate)}</td>
                            <td className="px-2 py-1.5">{dd(b.dueDate)} {od > 0 && <ChipTag tone={od > 30 ? 'r' : 'o'}>{od}d overdue</ChipTag>}</td>
                            <td className="px-2 py-1.5 text-right font-mono">{inr(pendingOf(b))} <span className="text-[var(--text-muted)]">{k.billSide}</span></td>
                            <td className="px-2 py-1.5 text-right">
                              {x ? <Input type="number" step="0.01" min={0} max={pendingOf(b)} className="ml-auto !w-[130px] text-right font-mono" value={x.amt || ''} placeholder="0.00"
                                onChange={(e) => setRefs(i, r.refs.map((y) => (y === x ? { ...y, amt: r2(Number(e.target.value) || 0) } : y)))} /> : <span className="text-[var(--text-muted)]">—</span>}
                            </td>
                            <td className="px-2 py-1.5 text-[var(--text-muted)]">{x ? k.refSide : ''}</td>
                          </tr>
                        );
                      })}
                      {ob.length === 0 && (
                        <tr><td colSpan={8} className="px-2 py-2 text-[var(--text-muted)]">{pend[i]?.isLoading ? 'Loading bills…' : 'No pending bills for this party — add a New Ref, Advance or On Account line below.'}</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {other.length > 0 && (
                  <div className="mt-1.5 overflow-x-auto rounded-lg border border-[var(--border-color)]">
                    <table className="w-full min-w-[460px] whitespace-nowrap text-xs">
                      <thead className="bg-white/[0.03] text-left text-[var(--text-muted)]">
                        <tr><th className="w-[140px] px-2 py-1.5">Type of Ref</th><th className="px-2 py-1.5">Name</th><th className="px-2 py-1.5 text-right">Amount</th><th /><th /></tr>
                      </thead>
                      <tbody>
                        {other.map(([x, j]) => (
                          <tr key={j} className="border-t border-[var(--border-color)]">
                            <td className="px-2 py-1.5">
                              <Select value={x.t} onChange={(e) => setRef(i, j, { t: e.target.value as Ref['t'], name: '' })}>
                                <option>New Ref</option><option>Advance</option><option>On Account</option>
                              </Select>
                            </td>
                            <td className="px-2 py-1.5">
                              {x.t === 'On Account' ? <span className="text-[var(--text-muted)]">—</span>
                                : <Input value={x.name} maxLength={60} placeholder={x.t === 'New Ref' ? 'Ref name' : 'e.g. order no.'} onChange={(e) => setRef(i, j, { name: e.target.value })} />}
                            </td>
                            <td className="px-2 py-1.5 text-right">
                              <Input type="number" step="0.01" min={0} className="ml-auto !w-[130px] text-right font-mono" value={x.amt || ''} placeholder="0.00"
                                onChange={(e) => setRef(i, j, { amt: r2(Number(e.target.value) || 0) })} />
                            </td>
                            <td className="px-2 py-1.5 text-[var(--text-muted)]">{k.refSide}</td>
                            <td className="px-2 py-1.5"><XBtn title="Remove line" onClick={() => setRefs(i, r.refs.filter((_, m) => m !== j))} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="mt-2 flex flex-wrap gap-2">
                  <SmBtn onClick={() => setRefs(i, [...r.refs, { t: 'New Ref', name: '', amt: 0 }])}>＋ New Ref</SmBtn>
                  <SmBtn onClick={() => setRefs(i, [...r.refs, { t: 'Advance', name: '', amt: 0 }])}>＋ Advance</SmBtn>
                  <SmBtn onClick={() => setRefs(i, [...r.refs, { t: 'On Account', name: '', amt: 0 }])}>＋ On Account</SmBtn>
                  {ob.length > 0 && <SmBtn onClick={() => setRow(i, { refs: fifo(i, r.amount) })}>⚡ Oldest bills first</SmBtn>}
                </div>
              </>
            )}
          </div>
        );
      })}
      <SmBtn onClick={() => set({ rows: [...s.rows, { key: seq++, party: '', amount: 0, refs: [] }] })}>＋ Add party</SmBtn>

      {acc && !isCash && (
        <>
          <Sec>Bank Allocation</Sec>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <F label="Transaction type">
              <Select value={s.mode} onChange={(e) => set({ mode: e.target.value })}>{modeOpts(acc).map((m) => <option key={m}>{m}</option>)}</Select>
            </F>
            <F label={`${s.mode === 'Cheque' ? 'Inst. no. (cheque)' : 'UTR / Ref'} *`}><Input value={s.ref} maxLength={60} onChange={(e) => set({ ref: e.target.value })} /></F>
            <F label={`Inst. date${s.mode === 'Cheque' && s.idate > s.date ? ' · post-dated' : ''}`}><Input type="date" value={s.idate} onChange={(e) => set({ idate: e.target.value })} /></F>
            {type === 'Receipt'
              ? (s.mode === 'Cheque' && <F label="Bank name (cheque drawn on)"><Input value={s.bankName} maxLength={120} placeholder="e.g. SBI, Panipat" onChange={(e) => set({ bankName: e.target.value })} /></F>)
              : <F label="Favouring name"><Input value={s.favouring} maxLength={200} placeholder={s.rows[0]?.party ? byId.get(s.rows[0].party as number)?.name : ''} onChange={(e) => set({ favouring: e.target.value })} /></F>}
            <F label={`Amount${charges ? ' (in bank)' : ''}`}><ReadBox value={inr(net)} /></F>
            {type === 'Receipt' && (
              <F label="Bank charges deducted">
                <Input type="number" step="0.01" min={0} className="text-right font-mono" value={s.charges || ''} placeholder="0" onChange={(e) => set({ charges: r2(Number(e.target.value) || 0) })} />
              </F>
            )}
          </div>
        </>
      )}

      <Msgs block={block} warn={warn} />
      <F label="Narration" className="mt-3">
        <Textarea rows={2} maxLength={1000} value={s.narr} onChange={(e) => set({ narr: e.target.value })}
          placeholder={`${k.narr} ${s.rows.map((r) => (r.party === '' ? '' : byId.get(r.party)?.name)).filter(Boolean).join(', ')}`} />
      </F>
      <Sec>Posting</Sec>
      <Posting lines={post} />
    </Modal>
  );
}
